"use client";

import Header from "@/components/common/Header";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import FloatingAIWidget from "@/components/ai/FloatingAIWidget";
// import { WebVitalsDashboard } from "@/components/common";
import { usePathname } from "next/navigation";
import { useMemo, useState, useEffect } from "react";

interface ClientLayoutProps {
  children: React.ReactNode;
  fontClasses?: string;
}

export default function ClientLayout({
  children,
  fontClasses = "",
}: ClientLayoutProps) {
  const pathname = usePathname();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  // const [isWebVitalsDashboardOpen, setIsWebVitalsDashboardOpen] =
  //   useState(false);

  // Listen for logout loading events
  useEffect(() => {
    const handleLogoutLoading = (event: Event) => {
      const customEvent = event as CustomEvent;
      setIsLoggingOut(customEvent.detail);
    };

    window.addEventListener("logout-loading", handleLogoutLoading);
    return () => {
      window.removeEventListener("logout-loading", handleLogoutLoading);
    };
  }, []);

  const instructorPaths = [
    "/instructors/dashboard",
    "/instructors/classes",
    "/instructors/bookings",
    "/instructors/analytics",
    "/instructors/event-management",
    "/instructors/gallery",
    "/instructors/notifications",
    "/instructors/reviews",
    "/instructors/chat",
    "/instructors/payment",
    "/instructors/profile",
    "/instructors/legal-agreement",
    "/instructors/help-center",
    "/instructors/contact-support",
    "/instructors/schedule",
    "/instructors/earnings",
    "/admin/dashboard",
    "/admin/instructors",
    "/admin/classes",
    "/admin/blogs",
    "/admin/settings",
    "/admin/help-center",
    "/admin/students",
    "/admin/community",
  ];

  // Calculate shouldShowHeader based on current pathname
  const shouldShowHeader = useMemo(() => {
    if (!pathname) return true; // Default to showing header if pathname is not available

    // Hide for all admin routes globally
    if (pathname.startsWith("/admin")) {
      return false;
    }

    // Hide for instructor dashboard routes, but NOT for public instructor profile pages
    if (pathname.startsWith("/instructors")) {
      // Show header for public instructor profile pages and instructors list page
      const isPublicInstructorProfile = /^\/instructors\/[a-f0-9]{24}$/.test(pathname);
      const isInstructorsListPage = pathname === "/instructors";

      if (isPublicInstructorProfile || isInstructorsListPage) {
        return true;
      }
      return false; // Hide for all other instructor dashboard routes
    }

    // Hide if current path is a student live class
    const isStudentLiveClass = /^\/classes\/[^/]+\/live/.test(pathname);

    return !isStudentLiveClass;
  }, [pathname]);

  return (
    <div className={`client-layout ${fontClasses}`}>
      {shouldShowHeader && <Header />}

      {/* Show logout loader overlay */}
      {isLoggingOut ? (
        <main className="client-layout-content min-h-screen bg-gradient-to-br from-slate-50 to-white flex items-center justify-center">
          <LoadingSpinner
            fullScreen={false}
            showText={true}
            text="Signing out..."
          />
        </main>
      ) : (
        <main className="client-layout-content">{children}</main>
      )}

      <FloatingAIWidget />
    </div>
  );
}
